// helpers/tracking.js

/**
 * Format phone number to E.164 format
 * Supports any country code
 * 
 * @param {string} rawPhone - Raw phone number input
 * @param {string} defaultCountryCode - Country code with + (e.g., '+33', '+1', '+44')
 * @returns {string} Formatted phone number in E.164 format
 */
export function formatPhoneE164(rawPhone, defaultCountryCode = '+33') {
  if (!rawPhone) return undefined;
  
  // Step 1: Remove all non-digit characters (keep only digits)
  let cleaned = rawPhone.replace(/\D/g, '');
  
  // Step 2: Extract the country code digits (remove the +)
  const countryCodeDigits = defaultCountryCode.replace('+', '');
  
  // Step 3: Remove the country code if it's at the beginning
  if (cleaned.startsWith(countryCodeDigits)) {
    cleaned = cleaned.substring(countryCodeDigits.length);
  }
  // Also handle if it starts with "00" + country code
  if (cleaned.startsWith('00' + countryCodeDigits)) {
    cleaned = cleaned.substring(2 + countryCodeDigits.length);
  }
  
  // Step 4: Format the number
  // If it starts with 0 (local format), replace it with the country code
  if (cleaned.startsWith('0')) {
    cleaned = defaultCountryCode + cleaned.substring(1);
  } 
  // If it doesn't have a country code, add it
  else if (!cleaned.startsWith(countryCodeDigits)) {
    cleaned = defaultCountryCode + cleaned;
  }
  // If it has the country code digits without the +, add the +
  else {
    cleaned = defaultCountryCode + cleaned;
  }
  
  return cleaned;
}

/**
 * Get country code from user data
 */
export function getCountryCode(user) {
  const countryMap = {
    'FR': '+33',
    'GB': '+44',
    'US': '+1',
    'ES': '+34',
    'DE': '+49',
    'IT': '+39',
    'BE': '+32',
    'CH': '+41',
    'NL': '+31',
    'PT': '+351',
    'PL': '+48',
    'SE': '+46',
    'NO': '+47',
    'DK': '+45',
    'FI': '+358',
    'IE': '+353',
    'AT': '+43',
    'GR': '+30',
    'HU': '+36',
    'ZA': '+27',
    'MA': '+212',
    'DZ': '+213',
    'TN': '+216'
  };
  
 // Try different sources in order of priority
  let countryCode = user?.country_of_residence || 
                    user?.nationality || 
                    user?.company_address?.country || 
                    'FR';
  
  // Normalize: uppercase and trim
  countryCode = countryCode.toUpperCase().trim();
  
  return countryMap[countryCode] || '+33';
}

/**
 * Format phone with auto-detected country code from user data
 */
export function formatPhoneWithCountry(rawPhone, user) {
  if (!rawPhone) return undefined;
  const countryCode = getCountryCode(user);
  return formatPhoneE164(rawPhone, countryCode);
}